import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  ButtonWithPopout,
  Theme,
  ThemeName,
  Icon,
  KeyArrowBracketLeftIcon,
  KeyArrowBracketRightIcon,
  KeyQuestionMarkIcon,
  Heading,
  KeyEnterIcon,
  KeyNumberOneIcon,
  KeyNumberTwoIcon,
  KeyNumberThreeIcon,
  KeyNumberNineIcon,
  KeyShiftIcon,
  KeyArrowDownIcon,
  KeyArrowUpIcon,
  KeyArrowRightIcon,
  KeyArrowLeftIcon,
  KeyNumberFourIcon,
  KeyNumberFiveIcon,
} from '@taopix/taopix-design-system';
import useKeyboardMode from '../../Hooks/useKeyboardMode';
import classNames from 'classnames';
import {LayoutSelector} from '../../Selectors';
import {useSelector} from 'react-redux';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useRegisterKeyboardCommand} from '../Context/KeyboardCommandsContext';
import {NumBool, PageStyleType, ViewMode} from '@taopix/taopix-core';
import {useExperience} from '../Experience/ExperienceProvider';
import {useTranslation} from 'react-i18next';

type keyboardTipLocalStorageObj = {
  [key: string]: boolean;
};

const KeyboardTips = () => {
  const keyboardMode = useKeyboardMode();
  const productLayout = useSelector(LayoutSelector);
  const ui = useUiState();
  const {experience} = useExperience();
  const {t} = useTranslation(['*']);
  const keyboardHelpOpenRef = useRef(ui.state.tips.keyboardHelp.open);

  // Helper to update keyboardHelp in context
  const setKeyboardHelp = (update: Partial<{open: boolean; currentTip: string | null}>) => {
    ui.dispatch({
      type: 'SET_KEYBOARD_HELP',
      payload: {
        ...ui.state.tips.keyboardHelp,
        ...update,
      },
    } as any);
  };

  const setKeyboardTipSeen = (id: string) => {
    // Write that the tip has been seen to local storage.
    const helpTipLocalStorage = localStorage.getItem('keyboardTip');
    const localStorageObj: keyboardTipLocalStorageObj = helpTipLocalStorage
      ? JSON.parse(helpTipLocalStorage)
      : {};
    localStorageObj[id as keyof keyboardTipLocalStorageObj] = true;
    localStorage.setItem('keyboardTip', JSON.stringify(localStorageObj));
  };

  useEffect(() => {
    keyboardHelpOpenRef.current = ui.state.tips.keyboardHelp.open;
    // Store the current tip as seen when the popout is closed (only the user can close it so we take this as a dismissal)
    if (!ui.state.tips.keyboardHelp.open && ui.state.tips.keyboardHelp.currentTip) {
      setKeyboardTipSeen(ui.state.tips.keyboardHelp.currentTip);
    }
  }, [ui.state.tips.keyboardHelp.open]);

  useRegisterKeyboardCommand({
    key: ['?', '/'],
    callback: () => {
      setKeyboardHelp({open: !keyboardHelpOpenRef.current});
    },
  });

  const buttonBarClasses = classNames('flex', 'shadow-md', 'overflow-hidden');

  // Close the tips popout when keyboard mode is turned off
  useEffect(() => {
    if (!keyboardMode) {
      setKeyboardHelp({open: false});
    }
  }, [keyboardMode]);

  const [allowLeftAndRightApply, setAllowLeftAndRightApply] = useState<boolean>(true);
  const [allowApplyAllPage, setAllowApplyAllPage] = useState<boolean>(false);

  useEffect(() => {
    const handleFocusChange = (event: FocusEvent) => {
      const id = (event.target as Element)?.id;
      if (!id?.includes('pagestyle')) return;
      const focusedPageStyleRef = id.split('-')[3];
      const pagestyle = productLayout?.pagestyles?.find(p => p.ref === focusedPageStyleRef);
      if (!pagestyle) return;

      // Show apply all left/right if we have are a single pagestyle on a spread page
      setAllowLeftAndRightApply(
        pagestyle.type === PageStyleType.Single && NumBool.True === productLayout.spreads
      );

      // show apply all pages for spread on spread and single on single
      setAllowApplyAllPage(
        pagestyle.type === PageStyleType.Spread ||
          (pagestyle.type === PageStyleType.Single && NumBool.False === productLayout.spreads)
      );
    };

    window.addEventListener('focusin', handleFocusChange);
    return () => window.removeEventListener('focusin', handleFocusChange);
  }, [productLayout]);

  const sectionClassNames = classNames('flex', 'flex-col', 'gap-md', 'p-md');
  const listClassNames = classNames('flex', 'flex-col', 'gap-sm');
  const tipClassNames = classNames('flex', 'items-center', 'gap-xs');
  const spanClasses = classNames('flex', 'items-center');
  // const controlIcon = navigator.language.startsWith('de') ? <KeyStrgIcon /> : <KeyCtrlIcon />;

  const generalTips = (
    <section className={sectionClassNames}>
      <Heading size={3}>{t('*:str_LabelNavigation', {defaultValue: 'Navigation'})}</Heading>
      <ul className={listClassNames}>
        <li>
          <p className={tipClassNames}>
            <span className={spanClasses}>
              <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
              <Icon
                icon={
                  ui.state.viewMode === ViewMode.Single ? (
                    <KeyArrowRightIcon />
                  ) : (
                    <KeyArrowDownIcon />
                  )
                }
                size="large"
                title="Icon for key 'Arrow down'"
              />
            </span>
            {t('*:str_ButtonNextPage')}
          </p>
        </li>
        <li>
          <p className={tipClassNames}>
            <span className={spanClasses}>
              <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
              <Icon
                icon={
                  ui.state.viewMode === ViewMode.Single ? <KeyArrowLeftIcon /> : <KeyArrowUpIcon />
                }
                size="large"
                title="Icon for key 'Arrow up'"
              />
            </span>
            {t('*:str_ButtonPreviousPage')}
          </p>
        </li>
        <li>
          <p className={tipClassNames}>
            <span className={spanClasses}>
              <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
              <Icon icon={<KeyNumberOneIcon />} size="large" title="Icon for key '1'" />
            </span>
            {t('*:str_LabelJumpToHeader', {defaultValue: 'Jump to header'})}
          </p>
        </li>
        {ui.state.viewMode !== ViewMode.Preview && (
          <>
            <li>
              <p className={tipClassNames}>
                <span className={spanClasses}>
                  <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
                  <Icon icon={<KeyNumberTwoIcon />} size="large" title="Icon for key '2'" />
                </span>
                {t('*:str_LabelJumpToSidebar', {defaultValue: 'Jump to sidebar'})}
              </p>
            </li>
            <li>
              <p className={tipClassNames}>
                <span className={spanClasses}>
                  <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
                  <Icon icon={<KeyNumberThreeIcon />} size="large" title="Icon for key '3'" />
                </span>
                {t('*:str_LabelJumpToWorkArea', {defaultValue: 'Jump to work area'})}
              </p>
            </li>
            <li>
              <p className={tipClassNames}>
                <span className={spanClasses}>
                  <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
                  <Icon icon={<KeyNumberFourIcon />} size="large" title="Icon for key '4'" />
                </span>
                {t('*:str_LabelJumpToPageOptions', {defaultValue: 'Jump to page options'})}
              </p>
            </li>
            {ui.state.zoomLevel === 100 &&
              [ViewMode.List, ViewMode.Single].includes(ui.state.viewMode) &&
              experience.EDITOR.pageEditing.allowUsersToToggleLocking && (
                <li>
                  <p className={tipClassNames}>
                    <span className={spanClasses}>
                      <Icon icon={<KeyShiftIcon />} size="large" title="Icon for key 'Shift'" /> +
                      <Icon icon={<KeyNumberFiveIcon />} size="large" title="Icon for key '5'" />
                    </span>
                    {t('*:str_LabelToggleAdvancedEditing', {
                      defaultValue: 'Toggle advanced editing',
                    })}
                  </p>
                </li>
              )}
          </>
        )}
      </ul>
    </section>
  );

  const tipMessage = useMemo(() => {
    switch (ui.state.tips.keyboardHelp.currentTip) {
      case 'layoutspalette':
      case 'layoutstip': {
        return (
          <section className={sectionClassNames}>
            <Heading size={3}>
              {t('*:str_LabelApplyingALayout', {defaultValue: 'Applying a layout'})}
            </Heading>
            <ul className={listClassNames}>
              {NumBool.True === productLayout.spreads && allowLeftAndRightApply && (
                <li>
                  <p className={tipClassNames}>
                    <span className="flex">
                      <Icon
                        icon={<KeyArrowBracketLeftIcon />}
                        size="large"
                        title="Icon for key '<'"
                      />
                      <Icon
                        icon={<KeyArrowBracketRightIcon />}
                        size="large"
                        title="Icon for key '>'"
                      />
                    </span>
                    <span>
                      {t('*:str_LabelSelectLeftOrRightPage', {
                        defaultValue: 'Select left or right page',
                      })}
                    </span>
                  </p>
                </li>
              )}
              <li>
                <p className={tipClassNames}>
                  <span className="flex">
                    <Icon icon={<KeyEnterIcon />} size="large" title="Icon for key 'Enter'" />
                  </span>
                  <span>
                    {t('*:str_LabelApplyToTheCurrentPage', {
                      defaultValue: 'Apply to the current page',
                    })}
                  </span>
                </p>
              </li>
              {productLayout.pagecount > 1 && (
                <>
                  {/* Need to hide or disable the first two if a spread style is selected or not a spread product */}
                  {NumBool.True === productLayout.spreads && allowLeftAndRightApply && (
                    <>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberOneIcon />}
                              size="large"
                              title="Icon for key '1'"
                            />
                          </span>
                          <span>{t('*:str_ButtonApplyToAllLeftPages')}</span>
                        </p>
                      </li>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberTwoIcon />}
                              size="large"
                              title="Icon for key '2'"
                            />
                          </span>
                          <span>{t('*:str_ButtonApplyToAllRightPages')}</span>
                        </p>
                      </li>
                    </>
                  )}
                  {allowApplyAllPage && (
                    <li>
                      <p className={tipClassNames}>
                        <span className="flex">
                          <Icon
                            icon={<KeyNumberThreeIcon />}
                            size="large"
                            title="Icon for key '3'"
                          />
                        </span>
                        <span>{t('*:str_ButtonApplytoAllPages')}</span>
                      </p>
                    </li>
                  )}
                </>
              )}
            </ul>
          </section>
        );
      }
      case 'picturespalette': {
        return (
          <section className={sectionClassNames}>
            <Heading size={3}>
              {t('*:str_LabelUsingPictures', {defaultValue: 'Using pictures'})}
            </Heading>
            <ul className={listClassNames}>
              <li>
                <p className={tipClassNames}>
                  <span className="flex">
                    <Icon icon={<KeyNumberOneIcon />} size="large" title="Icon for key '1'" /> ...
                    <Icon icon={<KeyNumberNineIcon />} size="large" title="Icon for key '9'" />
                  </span>
                  {t('*:str_LabelAddPictureToBox', {defaultValue: 'Add picture to box'})}
                </p>
              </li>
            </ul>
          </section>
        );
      }
      case 'backgroundspalette': {
        return (
          <section className={sectionClassNames}>
            <Heading size={3}>
              {t('*:str_LabelApplyingABackground', {defaultValue: 'Applying a background'})}
            </Heading>
            <ul className={listClassNames}>
              {NumBool.True === productLayout.spreads && (
                <li>
                  <p className={tipClassNames}>
                    <span className="flex">
                      <Icon
                        icon={<KeyArrowBracketLeftIcon />}
                        size="large"
                        title="Icon for key '<'"
                      />
                      <Icon
                        icon={<KeyArrowBracketRightIcon />}
                        size="large"
                        title="Icon for key '>'"
                      />
                    </span>
                    <span>
                      {t('*:str_LabelSelectLeftOrRightPage', {
                        defaultValue: 'Select left or right page',
                      })}
                    </span>
                  </p>
                </li>
              )}
              <li>
                <p className={tipClassNames}>
                  <span className="flex">
                    <Icon icon={<KeyEnterIcon />} size="large" title="Icon for key 'Enter'" />
                  </span>
                  <span>{t('*:str_LabelApplyToTheCurrentPage')}</span>
                </p>
              </li>
              {productLayout.pagecount > 1 && (
                <>
                  {NumBool.True === productLayout.spreads ? (
                    <>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberOneIcon />}
                              size="large"
                              title="Icon for key '1'"
                            />
                          </span>
                          <span>
                            {t('*:str_LabelApplyToTheCurrentSpread', {
                              defaultValue: 'Apply to the current spread',
                            })}
                          </span>
                        </p>
                      </li>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberTwoIcon />}
                              size="large"
                              title="Icon for key '2'"
                            />
                          </span>
                          <span>
                            {t('*:str_LabelApplyToAllSpreads', {
                              defaultValue: 'Apply to all spreads',
                            })}
                          </span>
                        </p>
                      </li>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberThreeIcon />}
                              size="large"
                              title="Icon for key '3'"
                            />
                          </span>
                          <span>{t('*:str_ButtonApplyToAllLeftPages')}</span>
                        </p>
                      </li>
                      <li>
                        <p className={tipClassNames}>
                          <span className="flex">
                            <Icon
                              icon={<KeyNumberFourIcon />}
                              size="large"
                              title="Icon for key '4'"
                            />
                          </span>
                          <span>{t('*:str_ButtonApplyToAllRightPages')}</span>
                        </p>
                      </li>
                    </>
                  ) : (
                    <li>
                      <p className={tipClassNames}>
                        <span className="flex">
                          <Icon icon={<KeyNumberOneIcon />} size="large" title="Icon for key '1'" />
                        </span>
                        <span>{t('*:str_ButtonApplytoAllPages')}</span>
                      </p>
                    </li>
                  )}
                </>
              )}
            </ul>
          </section>
        );
      }
      default: {
        return null;
      }
    }
  }, [ui.state.tips.keyboardHelp.currentTip, allowLeftAndRightApply, allowApplyAllPage]);

  return (
    <Theme name={ThemeName.Popouts} allowBorder={false} className={buttonBarClasses}>
      <ButtonWithPopout
        open={ui.state.tips.keyboardHelp.open && keyboardMode}
        buttonProps={{
          id: 'keyboard-tips-button',
          label: t('*:str_LabelKeyboardTips', {defaultValue: 'Keyboard tips'}),
          startIcon: <KeyQuestionMarkIcon />,
          arrow: 'down',
          buttonStyle: 'primary',
          size: 'small',
        }}
        popoutProps={{
          className: 'flex flex-col divide-y divide-themeBorderColour',
        }}
      >
        {tipMessage}
        {generalTips}
      </ButtonWithPopout>
    </Theme>
  );
};

export default KeyboardTips;
